import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bike, CheckCircle2, Coffee, Footprints, Info, Loader2, Package, UtensilsCrossed } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { ErrorState } from '@/components/ui/error-state';
import { SelectField } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { use_dining_profile, use_save_dining_profile } from '@/features/dining/hooks';
import type { DiningKind, DiningProfile } from '@/features/dining/types';
import { KIND_LABEL } from '@/features/dining/types';

const KIND_OPTIONS: { value: DiningKind; label: string }[] = [
  { value: 'PET_CAFE', label: KIND_LABEL.PET_CAFE },
  { value: 'PET_RESTAURANT', label: KIND_LABEL.PET_RESTAURANT },
  { value: 'PET_BAKERY', label: KIND_LABEL.PET_BAKERY },
];

type ToggleKey = 'dine_in' | 'delivery' | 'pickup' | 'walk_in';
const TOGGLES: { key: ToggleKey; label: string; hint: string; icon: LucideIcon }[] = [
  { key: 'dine_in', label: 'Dine-in', hint: 'Guests can sit and eat at your venue', icon: UtensilsCrossed },
  { key: 'walk_in', label: 'Walk-ins welcome', hint: 'No reservation needed to drop by', icon: Footprints },
  { key: 'delivery', label: 'Delivery', hint: 'You deliver orders to customers', icon: Bike },
  { key: 'pickup', label: 'Pickup', hint: 'Customers collect orders from you', icon: Package },
];

function Toggle({
  on,
  onToggle,
  label,
  hint,
  icon: Icon,
  testid,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  hint: string;
  icon: LucideIcon;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      data-testid={testid}
      className={cn(
        'flex items-center gap-3 rounded-input border-2 px-4 py-3 text-left transition-all duration-fast',
        on ? 'border-primary bg-primary/5 shadow-clay-sm' : 'border-input hover:border-primary/40',
      )}
    >
      <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-button', on ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground')}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
      <span
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors',
          on ? 'bg-primary' : 'bg-muted-foreground/25',
        )}
      >
        <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all', on ? 'left-[1.375rem]' : 'left-0.5')} />
      </span>
    </button>
  );
}

export function DiningSetupPage() {
  const profile_q = use_dining_profile();
  const save_m = use_save_dining_profile();
  const [form, set_form] = useState<DiningProfile | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (profile_q.data && !form) set_form(profile_q.data);
  }, [profile_q.data, form]);

  function patch<K extends keyof DiningProfile>(key: K, value: DiningProfile[K]) {
    set_saved(false);
    set_form((f) => (f ? { ...f, [key]: value } : f));
  }

  async function submit() {
    if (!form) return;
    set_error(null);
    if (form.opening_time >= form.closing_time) {
      return set_error('Opening time must be before closing time.');
    }
    try {
      await save_m.mutateAsync(form);
      set_saved(true);
    } catch (err) {
      set_error(form_error_message(err, 'Could not save your dining setup.'));
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Dining setup"
        description="Tell customers what kind of place you are and how they can enjoy it."
      />

      {profile_q.isLoading || !form ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : profile_q.error ? (
        <ErrorState data-testid="dining-setup-error" title="Couldn't load your setup" description={form_error_message(profile_q.error, 'Please try again.')} />
      ) : (
        <Card data-testid="dining-setup-form">
          <CardContent className="flex flex-col gap-5 p-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dining-kind" className="flex items-center gap-1.5">
                <Coffee className="h-4 w-4 text-primary" /> Venue type
              </Label>
              <SelectField
                id="dining-kind"
                data-testid="dining-kind"
                value={form.kind}
                onValueChange={(v) => patch('kind', v as DiningKind)}
                options={KIND_OPTIONS}
                className="max-w-xs"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>What you offer</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {TOGGLES.map((t) => (
                  <Toggle
                    key={t.key}
                    testid={`dining-toggle-${t.key}`}
                    on={form[t.key]}
                    onToggle={() => patch(t.key, !form[t.key])}
                    label={t.label}
                    hint={t.hint}
                    icon={t.icon}
                  />
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dining-open">Opening time</Label>
                <TimePicker id="dining-open" data-testid="dining-open" value={form.opening_time} onChange={(v) => patch('opening_time', v)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dining-close">Closing time</Label>
                <TimePicker id="dining-close" data-testid="dining-close" value={form.closing_time} onChange={(v) => patch('closing_time', v)} />
              </div>
            </div>

            {/*
              These hours are descriptive only — the slot engine builds bookable times from
              Availability rules, not from here. A vendor with a fully configured Dining Setup and
              an active offering had ZERO bookable slots on every date and no way to know why: the
              page said "Saved ✓" and they reasonably concluded they were open for business
              (BUG-085). Say it plainly and link to the page that actually opens the diary.
            */}
            <div className="flex items-start gap-2 rounded-input border-2 border-amber-300/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/30" data-testid="dining-availability-notice">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p>
                These times are shown to customers on your profile. They do <strong>not</strong> create
                bookable slots — set those on{' '}
                <Link to="/vendor/app/availability" className="font-semibold text-primary hover:underline">
                  Availability
                </Link>
                , or customers will see no times to book.
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="dining-setup-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={submit} variant="gradient" disabled={save_m.isPending} data-testid="dining-save">
                {save_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save setup
              </Button>
              {saved && (
                <span className="flex items-center gap-1.5 text-sm font-semibold text-success" data-testid="dining-saved">
                  <CheckCircle2 className="h-4 w-4" /> Saved
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
